/* eslint-disable @typescript-eslint/no-shadow */
import { useEffect, useState } from 'react';

import { useCombobox, resetIdCounter } from 'downshift';

import type { IOptions } from '../../../shared/utils/options';

import type { IInputDropdownProps } from '../_props';

import { useHoverOption } from './_useHoverOption';
import { useStateEvent } from './_useStateEvent';
import { useSearch } from './_useSearch';
import { useSelectItem } from './_useSelectItem';

type TUseDropdown = Pick<
  IInputDropdownProps,
  'onChangeText' | 'onChangeSelect' | 'options' | 'filterable' | 'isSelect' | 'id' | 'defaultValue' | 'searchRules'
>;

export const useDropdown = ({
  onChangeText,
  onChangeSelect,
  filterable,
  options,
  isSelect,
  id,
  defaultValue,
  searchRules,
}: TUseDropdown) => {
  resetIdCounter();

  const [listOptions, setListOptions] = useState<IOptions[]>(options);

  const defaultSearchRules = {
    filterRule: searchRules?.filterRule || (() => true),
    sortRule: searchRules?.sortRule || (() => () => 1),
    missingOptionRule: searchRules?.missingOptionRule || (() => []),
  };

  useEffect(() => {
    const filteredOptions = options
      .filter((option) => defaultSearchRules.filterRule(option, defaultValue))
      .sort(defaultSearchRules.sortRule(defaultValue));

    setListOptions(filteredOptions);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options, defaultValue]);

  /**
   * Composition Hooks
   */

  const { handleHoverOption } = useHoverOption({ listOptions });
  const { handleFocus, handleBlur, propagateImmediateSearch } = useStateEvent({
    filterable,
    isSelect,
    searchRules: defaultSearchRules,
  });
  const { handleSearchFilter, propagateSearch } = useSearch({
    filterable,
    options,
    isSelect,
    searchRules: defaultSearchRules,
  });
  const { handleSelectItem, propagateSelectItem } = useSelectItem({
    filterable,
    options,
    isSelect,
    searchRules: defaultSearchRules,
  });

  /**
   * Combobox Hook
   */

  const {
    isOpen,
    getLabelProps,
    getInputProps,
    getMenuProps,
    getItemProps,
    highlightedIndex,
    selectedItem,
    inputValue,
    setInputValue,
    selectItem,
    getToggleButtonProps,
  } = useCombobox({
    id,
    items: listOptions,
    itemToString: (item) => (item ? item.label : ''),

    defaultHighlightedIndex: 0,
    onHighlightedIndexChange: ({ highlightedIndex, type }) => {
      handleHoverOption({ type, highlightedIndex, setInputValue });
    },

    onStateChange: ({ selectedItem: toBeSelectedItem, type }) => {
      handleFocus({ type, inputValue, setListOptions, options });
      handleBlur({ type, inputValue, toBeSelectedItem, setInputValue, selectItem, onChangeText });

      propagateImmediateSearch({ type, inputValue, toBeSelectedItem, setInputValue, selectItem, onChangeText });
    },

    onInputValueChange: ({ inputValue, type }) => {
      handleSearchFilter({ type, inputValue, setListOptions });

      propagateSearch({ type, inputValue, onChangeText });
    },

    onSelectedItemChange: ({ selectedItem, type }) => {
      handleSelectItem({ selectedItem, setListOptions });

      propagateSelectItem({ selectedItem, onChangeSelect, type });
    },
    inputValue: defaultValue,
  });

  /**
   * Return
   */

  return {
    isOpen,
    getLabelProps,
    getInputProps,
    getMenuProps,
    getItemProps,
    highlightedIndex,
    selectedItem,
    inputValue,
    listOptions,
    getToggleButtonProps,
  };
};

export default useDropdown;
